import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { logout } from '../utils/auth';

export interface ExerciseDetail {
  name: string;
  description?: string;
  mediaUrl?: string;
  skipped?: boolean;
  done?: boolean;
  metrics?: Record<string, { value: number; change?: number }> & { speed?: 'slow' | 'normal' | 'fast' };
}

export interface SessionDetail {
  id: number;
  title: string;
  description?: string;
  durationMinutes?: number;
  exercises: ExerciseDetail[];
}

export interface TypedExerciseResponseAPI {
  typedExercise: {
    id: number;
    type: 'MACHINE' | 'MACHINE_POWER_BASED' | 'CARDIO' | 'STRENGTH' | 'OTHER' | 'CONSULTATION';
    concentric?: number;
    eccentric?: number;
    delta?: number;
    rate?: number;
    reps?: number;
    km?: number;
    pulse?: number;
    brakeW?: number;
    brakeNm?: number;
    timer?: number;
    mode?: number;
    exercise: {
      id: number;
      name: string;
      duration: number;
      type: string;
      description?: string;
      mediaUrl?: string;
      machineType?: string | null;
    };
    trainingStepId: number;
  };
}

const buildMetrics = (t: TypedExerciseResponseAPI['typedExercise']): ExerciseDetail['metrics'] => {
  const metrics: any = {};
  if (t.type === 'MACHINE') {
    if (t.concentric != null) metrics.concentric = { value: t.concentric };
    if (t.eccentric != null) metrics.eccentric = { value: t.eccentric };
    if (t.delta != null) metrics.delta = { value: t.delta };
    if (t.reps != null) metrics.reps = { value: t.reps };
    if (t.exercise?.duration != null) metrics.time = { value: t.exercise.duration };
    if (t.rate != null) metrics.speed = t.rate === 0 ? 'slow' : t.rate === 1 ? 'normal' : 'fast';
  }
  if (t.type === 'MACHINE_POWER_BASED') {
    if (t.brakeW != null) metrics.w = { value: t.brakeW };
    if (t.brakeNm != null) metrics.nm = { value: t.brakeNm };
    if (t.timer != null) metrics.additionalTime = { value: t.timer };
    if (t.mode != null) metrics.mode = { value: t.mode };
    if (t.exercise?.duration != null) metrics.time = { value: t.exercise.duration };
    if (t.rate != null) metrics.speed = t.rate === 0 ? 'slow' : t.rate === 1 ? 'normal' : 'fast';
  }
  if (t.type === 'CARDIO') {
    if (t.km != null) metrics.kilometry = { value: t.km };
    if (t.pulse != null) metrics.puls = { value: t.pulse };
    if (t.exercise?.duration != null) metrics.time = { value: t.exercise.duration };
    if (t.reps != null) metrics.reps = { value: t.reps };
    if (t.rate != null) metrics.speed = t.rate === 0 ? 'slow' : t.rate === 1 ? 'normal' : 'fast';
  }
  if (t.type === 'STRENGTH') {
    if (t.reps != null) metrics.reps = { value: t.reps };
    if (t.exercise?.duration != null) metrics.time = { value: t.exercise.duration };
    if (t.rate != null) metrics.speed = t.rate === 0 ? 'slow' : t.rate === 1 ? 'normal' : 'fast';
  }
  if (t.type === 'OTHER') {
    if (t.pulse != null) metrics.puls = { value: t.pulse };
    if (t.rate != null) metrics.speed = t.rate === 0 ? 'slow' : t.rate === 1 ? 'normal' : 'fast';
    if (t.reps != null) metrics.reps = { value: t.reps };
    if (t.exercise?.duration != null) metrics.time = { value: t.exercise.duration };
  }
  if (t.type === 'CONSULTATION') {
    if (t.exercise?.duration != null) metrics.time = { value: t.exercise.duration };
  }
  return metrics;
};

const queryOptions = (typedExerciseId: number, type: string, trainingStepId: number) => ({
  queryKey: ['session', typedExerciseId, type, trainingStepId],
  queryFn: async () => {
    if (!typedExerciseId || !type) throw new Error('Missing ids');
    const apiKey = localStorage.getItem('apiKey');
    try {
      const { data } = await axios.get<TypedExerciseResponseAPI>(
        `${process.env.REACT_APP_STUDIO_API_URL}/pwa/user-training/exercise?typedexerciseId=${typedExerciseId}&type=${type}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
          },
        }
      );
      //console.log('[useSession] raw typed exercise response', data);
      const t = data.typedExercise;
      const detail: SessionDetail = {
        id: t.trainingStepId,
        title: t.exercise.name,
        description: t.exercise.description,
        durationMinutes: t.exercise.duration || 0,
        exercises: [
          {
            name: t.exercise.name,
            description: t.exercise.description,
            mediaUrl: t.exercise.mediaUrl || undefined,
            metrics: buildMetrics(t),
          },
        ],
      };
      //console.log('[useSession] mapped detail', detail);
      return detail;
    } catch (error: any) {
      if (error.response && error.response.status === 401 && error.response.data.error === 'Invalid or missing API key') {
        logout();
      }
      throw error;
    }
  },
  enabled: !!typedExerciseId && !!type,
}) as any;

export const useSession = (typedExerciseId: number, type: string, trainingStepId: number) => {
  return useQuery<SessionDetail, Error>(queryOptions(typedExerciseId, type, trainingStepId));
};


